<script setup>
// ========================================
// Imports
// ========================================
import { useDisplay } from 'vuetify'
import { until, useDebounceFn } from '@vueuse/core'
import TabHeader from '@/components/TabHeader.vue'
import ConversationThread from '@/components/messages/ConversationThread.vue'
import CommunicationNotice from '@/components/messages/CommunicationNotice.vue'
import AddNewCustomerDialog from '@/components/event/dialogs/AddNewCustomerDialog.vue'
import { useCustomerStore } from '@/stores/CustomersStore'
import { useModalStore } from '@/stores/ModalStore'
import { userColor } from '@/utils/colors'
import emitter from '@/utils/emitter'
import { extractDate, extractDateTime } from '@/utils/datetime'
import { formatPhone, getInitials } from '@/utils/strings'

// Keeps "Communication" highlighted in the menu
definePage({ meta: { navActiveLink: 'communication' } })

// ========================================
// Composables & Stores
// ========================================
const { t } = useI18n()
const { mobile, smAndDown, mdAndDown } = useDisplay()
const route = useRoute()
const router = useRouter()
const customerStore = useCustomerStore()
const modalStore = useModalStore()

// ========================================
// State
// ========================================
const conversations = ref([])
const meta = ref({})
const isLoading = ref(true)
const isLoadingMore = ref(false)
const notice = ref(null) // data.notice from BE - shown above the content
const search = ref('')
const filter = ref('all') // all | unread | read - applied to the loaded conversations
const unreadOnLoad = ref(new Set()) // client ids that were unread when the list was fetched
const knownClients = ref({}) // every client seen on the list - keeps the open conversation's name when a search hides it

// Search goes to BE from 3 characters - shorter input lists every conversation and shows a hint
const SEARCH_MIN_CHARS = 3
const searchQuery = computed(() => {
  const value = search.value?.trim() || ''

  return value.length >= SEARCH_MIN_CHARS ? value : ''
})
// const searchHint = computed(() => {
//   const length = search.value?.trim().length || 0
//
//   return length && length < SEARCH_MIN_CHARS ? t('messages_page.search_min_chars') : ''
// })

// Open conversation is kept in the URL (?client=ID), so a refresh or a link opens it
const selectedId = computed(() => Number(route.query.client) || null)

// One name string for display, initials and avatar color, so the list and the conversation match
const fullName = client => `${client.name} ${client.surname}`.trim()

// ========================================
// Conversations list
// ========================================
const PER_PAGE = 100
let lastRequestId = 0

/**
 * Loads the first page of conversations, or the next one when loadMore is true.
 * A refresh reloads as many conversations as are already loaded, so "Load more" pages aren't lost.
 * Only the latest request updates the list, so fast typing in search never shows stale results.
 */
const fetchConversations = async (loadMore = false) => {
  //console.warn('fetchConversations')
  const requestId = ++lastRequestId
  const perPage = loadMore ? PER_PAGE : Math.max(PER_PAGE, conversations.value.length)
  const params = new URLSearchParams({ perPage })
  if (searchQuery.value) params.append('search', searchQuery.value)
  if (loadMore) {
    params.append('before', meta.value.nextBefore)
    params.append('beforeSalonerClientId', meta.value.nextBeforeSalonerClientId)
  }

  if (loadMore) isLoadingMore.value = true
  else isLoading.value = true

  try {
    const response = await $backend(`/communication/conversations?${params}`, { method: 'GET' })
    if (requestId !== lastRequestId || !response?.success) return

    const page = response.data?.conversations || []
    conversations.value = loadMore ? [...conversations.value, ...page] : page
    if (!loadMore) unreadOnLoad.value = new Set()
    meta.value = response.meta || {}
    notice.value = response.data?.notice || null
    page.forEach(conversation => {
      knownClients.value[conversation.salonerClient.id] = conversation.salonerClient
      if (conversation.hasUnread) unreadOnLoad.value.add(conversation.salonerClient.id)
    })
  } catch (e) {
    // aborted or failed - $backend already showed the error
  } finally {
    if (requestId === lastRequestId) {
      isLoading.value = false
      isLoadingMore.value = false
    }
  }
}

// New search starts from the first page - drop the loaded pages of the previous one.
// Watches the sent query, so typing the 1st and 2nd character does not refetch
watch(searchQuery, useDebounceFn(() => {
  conversations.value = []
  fetchConversations()
}, 400))

// "Unread" uses the state from the last fetch, so a conversation doesn't vanish as soon as it's opened (and read).
// Clicking a chip refetches the list, which drops the ones read in the meantime.
const visibleConversations = computed(() => {
  if (filter.value === 'all') return conversations.value
  if (filter.value === 'unread') return conversations.value.filter(conversation => unreadOnLoad.value.has(conversation.salonerClient.id))

  return conversations.value.filter(conversation => !conversation.hasUnread)
})

// ponytail: counts only the loaded pages - use a BE total if the list gets a real unread counter
const unreadCount = computed(() => conversations.value.filter(conversation => conversation.hasUnread).length)

// Today: HH:mm, this year: day.month HH:mm, older: full date HH:mm
const formatListDate = (dateTime) => {
  const [date, time] = dateTime.split(' ')
  const [year, month, day] = date.split('-')
  const hoursMinutes = time.slice(0, 5)
  if (date === extractDate(new Date(), '-')) return hoursMinutes

  return year === String(new Date().getFullYear())
    ? `${day}.${month} ${hoursMinutes}`
    : `${day}.${month}.${year} ${hoursMinutes}`
}

// ========================================
// Selected conversation
// ========================================
const selectedClient = computed(() => {
  if (!selectedId.value) return null

  const client = knownClients.value[selectedId.value]
  const name = client ? fullName(client) : ''

  return {
    id: selectedId.value,
    name,
    phone: client?.phone || '',
    color: userColor(name, selectedId.value),
  }
})

const openConversation = (id) => {
  if (id === selectedId.value) return

  // Push when opening from the list, so the back gesture returns to it; replace when switching
  router[selectedId.value ? 'replace' : 'push']({ query: { client: id } })
}

const closeConversation = () => {
  // Go back to the list entry when we came from it, otherwise (direct link) just drop the param
  if (window.history.state?.back === route.path) router.back()
  else router.replace({ query: {} })
}

// Conversation couldn't be loaded (e.g. client not found) - back to "select a conversation" instead of a thread with no client to send to
const onLoadFailed = (id) => {
  if (id === selectedId.value) closeConversation()
}

// Thread was fetched - BE marked it as read (ConversationThread reads its notifications)
const markAsRead = (id) => {
  const conversation = conversations.value.find(conversation => conversation.salonerClient.id === id)
  if (conversation) conversation.hasUnread = false
}

// Move the conversation to the top with its newest message - just sent, or fetched by the thread's polling
const onNewMessage = (id, lastMessage) => {
  const index = conversations.value.findIndex(conversation => conversation.salonerClient.id === id)
  if (index === -1) return fetchConversations() // opened from a link, not on the loaded list

  const [conversation] = conversations.value.splice(index, 1)
  conversation.lastMessage = lastMessage
  conversation.hasUnread = false
  conversations.value.unshift(conversation)
}

const onMessageSent = (id, message) => onNewMessage(id, { conversationTime: extractDateTime(new Date()), message, direction: 1 })

// ========================================
// New message - existing SMS dialog
// ========================================
const openNewMessage = () => {
  customerStore.clearSelectedCustomer() // SmsDialog pre-fills the customer kept in the store
  modalStore.openModal('sendSmsToCustomer', 'newMessageBtn')
}

const thread = ref()

// Sent from the SMS dialog - open the recipient's conversation and refresh the list with it
const onSmsSent = async (id) => {
  // Already open: fetch the new message now - the thread moves the conversation to the top itself
  if (id === selectedId.value) return thread.value?.refresh()

  openConversation(id)
  await fetchConversations()
  // Scroll to conversation in the left panel
  await until(() => isLoading.value).toBe(false).then(async () => {
    const conversationEl = document.querySelector('.scid-'+selectedId.value)
    console.log('conversationEl', conversationEl)
    if (conversationEl) {
      conversationEl?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
    // else { // CLient not found on the list - let's search for it
    //   search.value = selectedId.value
    //   await nextTick()
    //   fetchConversations()
    // }
  })
}

emitter.on('sms-sent', onSmsSent)
onUnmounted(() => emitter.off('sms-sent', onSmsSent))

onMounted(async () => {
  await fetchConversations()
  // Scroll to conversation in the left panel
  await until(() => isLoading.value).toBe(false).then(() => {
    const conversationEl = document.querySelector('.scid-'+selectedId.value)
    if (conversationEl) {
      conversationEl?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  },
  )


})
</script>

<template>
  <TabHeader
    :title="$t('messages')"
    :breadcrumbs="[
      { title: $t('communication'), to: '/communication' },
      { title: $t('messages'), to: '/communication/messages' },
    ]"
  >
<!--    <VBtn-->
<!--      variant="outlined"-->
<!--      color="secondary"-->
<!--      class="ml-auto mr-2"-->
<!--      prepend-icon="ri-history-line"-->
<!--      :aria-label="$t('history')"-->
<!--      :to="{ name: 'communication-history' }"-->
<!--    >-->
<!--      <template #prepend>-->
<!--        <VIcon-->
<!--          :size="mobile ? 18 : 20"-->
<!--          :class="{ 'mr-n2': mobile }"-->
<!--        />-->
<!--      </template>-->
<!--      <span v-if="!mobile">-->
<!--        {{ $t('history') }}-->
<!--      </span>-->
<!--    </VBtn>-->
    <span class="ml-auto mr-2">
      <VBtn
        variant="outlined"
        color="secondary"
        :size="mobile ? 'small' : 'default'"
        to="/communication"
      >
        <VIcon
          icon="ri-arrow-left-line"
          size="18"
          :class="mobile ? '' : 'mr-1'"
        />
        <span v-if="!mobile">{{ $t('back') }}</span>
      </VBtn>
      <VTooltip
        v-if="!mobile"
        activator="parent"
      >
        {{ $t('back') }}
      </VTooltip>
    </span>
    <VBtn
      id="newMessageBtn"
      color="primary"
      :size="mobile ? 'small' : 'default'"
      prepend-icon="ri-add-line"
      :aria-label="$t('messages_page.new_message')"
      @click="openNewMessage"
    >
      <template #prepend>
        <VIcon
          :size="mobile ? 18 : 24"
          :class="{ 'mr-n2': mobile }"
        />
      </template>
      <span v-if="!mobile">
        {{ $t('messages_page.new_message') }}
      </span>
    </VBtn>
  </TabHeader>

  <VContainer
    fluid
    class="pt-4"
    :class="{ 'px-0': mdAndDown, 'px-15': !mdAndDown, 'pb-0': smAndDown }"
  >
    <CommunicationNotice :notice="notice" />

    <VCard
      variant="elevated"
      elevation="6"
      :flat="smAndDown"
      class="d-flex"
      :class="smAndDown ? 'messages-card--mobile overflow-visible' : 'messages-card'"
    >
      <!-- 👉 Conversations list -->
      <div
        class="messages-column d-flex flex-column bg-surface"
        :class="smAndDown ? 'flex-grow-1 mx-n4' : 'messages-list border-e'"
      >
        <div
          class="d-flex flex-column ga-3 pa-4 border-b bg-surface"
          :class="{ 'messages-list__head--sticky': smAndDown }"
        >
          <VTextField
            v-model="search"
            :placeholder="$t('type_at_least_3chars')"
            :label="$t('messages_page.search_placeholder')"
            prepend-inner-icon="ri-search-line"
            density="compact"
            clearable
            hide-details="auto"
          />
<!--          :messages="searchHint"-->
          <VChipGroup
            v-model="filter"
            class="chip-group-custom w-100 ga-0"
            selected-class="v-chip--selected"
            variant="outlined"
            color="primary"
            mandatory
            center-active
            scroll-to-active
            filter
            :show-arrows="mobile"
          >
            <VChip
              value="all"
              @click="fetchConversations(false)"
            >
              {{ $t('messages_page.filter_all') }}
            </VChip>
            <VChip
              value="unread"
              @click="fetchConversations(false)"
            >
              {{ $t('messages_page.filter_unread') }}
              <VBadge
                v-if="unreadCount"
                :content="unreadCount"
                color="primary"
                class="ml-2"
                inline
              />
            </VChip>
            <VChip
              value="read"
              @click="fetchConversations(false)"
            >
              {{ $t('messages_page.filter_read') }}
            </VChip>
          </VChipGroup>
        </div>

        <div
          class="messages-list__items flex-grow-1"
          :class="{ 'overflow-y-auto': !smAndDown }"
        >
          <template v-if="isLoading">
            <VSkeletonLoader
              v-for="n in 6"
              :key="n"
              type="list-item-avatar-two-line"
            />
          </template>
          <template v-else>
            <VList class="py-0">
              <VListItem
                v-for="conversation in visibleConversations"
                :key="conversation.salonerClient.id"
                class="border-b py-3"
                :class="{
                  'messages-list__item--active': conversation.salonerClient.id === selectedId,
                  [`scid-${conversation.salonerClient.id}`]: true,
                }"
                @click="openConversation(conversation.salonerClient.id)"
              >
                <template #prepend>
                  <VAvatar
                    :color="userColor(fullName(conversation.salonerClient), conversation.salonerClient.id)"
                    variant="tonal"
                    size="40"
                    class="text-body-2 font-weight-medium"
                  >
                    <span v-if="fullName(conversation.salonerClient)">{{ getInitials(fullName(conversation.salonerClient)) }}</span>
                    <VIcon
                      v-else
                      icon="ri-user-line"
                    />
                  </VAvatar>
                </template>

                <VListItemTitle :class="conversation.hasUnread ? 'font-weight-bold' : 'font-weight-medium'">
                  {{ fullName(conversation.salonerClient) }}
                </VListItemTitle>
                <VListItemSubtitle class="mt-1">
                  {{ formatPhone(conversation.salonerClient.phone) }}
                </VListItemSubtitle>

                <template #append>
                  <div class="d-flex flex-column align-end ga-2">
                    <span
                      v-if="conversation.lastMessage?.conversationTime"
                      class="text-caption text-medium-emphasis"
                    >
                      {{ formatListDate(conversation.lastMessage.conversationTime) }}
                    </span>
                    <!-- Unread incoming message: red chip instead of a dot -->
                    <VChip
                      v-if="conversation.lastMessage"
                      size="x-small"
                      :color="conversation.hasUnread ? 'primary' : (conversation.lastMessage.direction === 1 ? 'green' : undefined)"
                      :prepend-icon="conversation.hasUnread ? 'ri-mail-unread-fill' : conversation.lastMessage.direction === 1 ? 'ri-arrow-right-up-line' : 'ri-mail-open-line' "
                    >
                      <template #prepend>
                        <VIcon size="16" />
                      </template>
                      {{ conversation.hasUnread ? $t('messages_page.history.unread') : conversation.lastMessage.direction === 2 ? $t('messages_page.direction_in') : $t('messages_page.direction_out') }}
                    </VChip>
                  </div>
                </template>
              </VListItem>
            </VList>

            <div
              v-if="!visibleConversations.length"
              class="pa-8 text-center text-body-2 text-medium-emphasis"
            >
              {{ $t('messages_page.no_conversations') }}
            </div>

            <div
              v-if="meta.nextBefore"
              class="pa-4"
            >
              <VBtn
                block
                variant="outlined"
                color="secondary"
                :loading="isLoadingMore"
                @click="fetchConversations(true)"
              >
                {{ $t('messages_page.load_more') }}
              </VBtn>
            </div>
          </template>
        </div>
      </div>

      <!-- 👉 Conversation -->
      <ConversationThread
        v-if="selectedClient"
        ref="thread"
        :key="selectedClient.id"
        :client="selectedClient"
        :show-notice="mobile"
        @close="closeConversation"
        @read="markAsRead"
        @sent="onMessageSent"
        @new-message="onNewMessage"
        @failed="onLoadFailed"
      />
      <div
        v-else-if="!smAndDown"
        class="flex-grow-1 d-flex flex-column align-center justify-center ga-3 pa-10 text-center bg-chat-bg"
      >
        <VAvatar
          size="64"
          class="border bg-surface"
        >
          <VIcon
            icon="ri-chat-3-line"
            size="28"
            class="text-medium-emphasis"
          />
        </VAvatar>
        <div class="text-h6">
          {{ $t('messages_page.select_conversation') }}
        </div>
        <div class="messages-empty__hint text-body-2 text-medium-emphasis">
          {{ $t('messages_page.read_on_open') }}
        </div>
        <VBtn
          v-if="unreadCount"
          variant="outlined"
          color="primary"
          class="mt-1"
          @click="filter = 'unread'"
        >
          {{ $t('messages_page.show_unread') }}
          <VBadge
            :content="unreadCount"
            color="primary"
            class="ml-3"
            bordered
            inline
          />
        </VBtn>
      </div>
    </VCard>
  </VContainer>

</template>

<style scoped lang="scss">
.messages-card {
  // ponytail: fixed offset of navbar + TabHeader, tune it if the page header changes
  height: calc(100vh - 284px); // fallback for Chrome < 108, which drops the dvh line below
  height: calc(100dvh - 284px);
  min-height: 410px;
}

// VCard's z-index creates a stacking context - the full-screen conversation inside must cover the navbar
.messages-card--mobile {
  z-index: auto;
}

// Flex items default to min-width: auto - without this the chip row's full width stretches the column past the screen
.messages-column {
  min-width: 0;
}

.messages-list {
  flex: 0 0 360px;
}

.messages-list__head--sticky {
  position: sticky;
  z-index: 2;
  inset-block-start: 0;
}

.messages-list__items {
  min-height: 0;
}

.messages-list__item--active {
  background: rgba(var(--v-theme-primary), 0.06);
  box-shadow: inset 3px 0 0 rgb(var(--v-theme-primary));
}

.messages-empty__hint {
  max-inline-size: 340px;
}


body:has(.conversation-thread--fullscreen) {
  overflow: hidden;
  .messages-list__items, .messages-card--mobile {
    overflow: hidden;
  }
}
.conversation-thread--fullscreen {
  overflow-y: auto;
  overscroll-behavior: contain;
}
</style>
